import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { applyDeliveryToBom, addDocketLinesToBom } from "./bom";   // mig 501 — a saved delivery ticks the Site BOM register
import { Camera, FileUp, Truck, Plus, Trash2, Sparkles, ArrowLeft, ExternalLink, AlertTriangle, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Field, Input, Select, Textarea, Spinner, Pill } from "@/components/ui";
import {
  type DeliveryHeader, type DeliveryLine, type DeliveryRow, type DeliverySource, type LineStatus, type DocketRead,
  DELIVERY_SELECT, SOURCE_LABEL, LINE_STATUS_LABEL,
  emptyHeader, newLine, uploadDocket, readDocket, docketSignedUrl, saveDelivery, rowToForm,
} from "./deliveries";

// =============================================================================
// TakeDeliveryScreen — /capture/delivery  (?project=<id> presets the job,
// ?id=<delivery_id> edits an existing one)
// =============================================================================
// Materials tracking piece 1 (mig 382). James, 19 Sep 2026: "when the guys
// take a delivery they snap the slip … which site it's for and what was
// delivered when / who signed it / uploaded it into Ops." Replaces the
// phone-scan → email-to-self → folder routine he called tedious.
//
// Flow: snap or pick the docket (photo or a scanned PDF) → it uploads to
// delivery-dockets and the worker reads it (/deliveries/ai-extract) → the
// header and lines pre-fill → the person confirms the job, who signed,
// where it came in, fixes anything the read got wrong → Save. The read never
// writes; save goes through app.save_material_delivery, which auto-matches
// the slip's client project number (APD Mirage / Ventia R009) to a project
// carrying the same client_project_ref.
//
// Works for the APD/Telstra CEVA slip (depot delivery) and the Ventia/NBN
// pickup docket (client_pickup) alike — one form, the source radio tells
// them apart.
// =============================================================================

interface ProjectOption {
  project_id: string; project_code: string; project_name: string; status: string;
  site_id: string | null; client_project_ref: string | null;
  site: { site_name: string; carrier_site_id: string | null } | null;
}

type ReadStatus = "idle" | "uploading" | "reading" | "done" | "error";

export default function TakeDeliveryScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const presetProject = params.get("project");
  const editId = params.get("id");
  const isManagerPlus = hasRole(user, "manager");

  const [header, setHeader] = useState<DeliveryHeader>(() => emptyHeader(user?.employee_id ?? null));
  const [lines, setLines] = useState<DeliveryLine[]>([]);
  const [docPaths, setDocPaths] = useState<string[]>([]);
  const [docUrls, setDocUrls] = useState<Record<string, string>>({});
  const [readStatus, setReadStatus] = useState<ReadStatus>("idle");
  const [readError, setReadError] = useState<string | null>(null);
  const [readInfo, setReadInfo] = useState<{ confidence: number; notes: string | null; handwritten: string | null } | null>(null);
  const [aiRaw, setAiRaw] = useState<{ confidence: number; raw: unknown } | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadedEdit, setLoadedEdit] = useState(false);
  const cameraInput = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // once the user record lands, default "received by" to me
  useEffect(() => {
    if (user?.employee_id && !editId) {
      setHeader(h => h.received_by_employee_id ? h : { ...h, received_by_employee_id: user.employee_id });
    }
  }, [user?.employee_id, editId]);

  // ---- options -----------------------------------------------------------------
  const { data: projects = [] } = useQuery({
    queryKey: ["delivery_project_options"],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, status, site_id, client_project_ref, site:site_id ( site_name, carrier_site_id )")
        .in("status", ["scheduled", "mobilising", "live", "acceptance_pending", "on_hold"])
        .order("project_code");
      if (error) throw error;
      return (data ?? []) as unknown as ProjectOption[];
    },
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["delivery_employee_options"],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, end_date")
        .is("end_date", null)
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as Array<{ employee_id: string; first_name: string; last_name: string }>;
    },
  });

  // ---- edit mode: load the existing delivery -----------------------------------
  const { data: existing } = useQuery({
    queryKey: ["delivery", editId],
    enabled: !!editId && !!user,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("material_deliveries").select(DELIVERY_SELECT).eq("delivery_id", editId!).single();
      if (error) throw error;
      return data as unknown as DeliveryRow;
    },
  });
  useEffect(() => {
    if (!existing || loadedEdit) return;
    const f = rowToForm(existing);
    setHeader(f.header); setLines(f.lines); setDocPaths(existing.document_paths ?? []); setLoadedEdit(true);
  }, [existing, loadedEdit]);

  // preset project from ?project=
  useEffect(() => {
    if (!presetProject || editId || header.project_id) return;
    const p = projects.find(x => x.project_id === presetProject);
    if (p) applyProject(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetProject, projects.length]);

  // signed URLs for the docket previews
  useEffect(() => {
    let alive = true;
    (async () => {
      const next: Record<string, string> = {};
      for (const p of docPaths) {
        if (docUrls[p]) { next[p] = docUrls[p]; continue; }
        const u = await docketSignedUrl(p);
        if (u) next[p] = u;
      }
      if (alive) setDocUrls(next);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docPaths.join("|")]);

  function applyProject(p: ProjectOption | null) {
    setHeader(h => ({
      ...h,
      project_id: p?.project_id ?? null,
      site_id: p?.site_id ?? null,
      client_project_ref: h.client_project_ref || (p?.client_project_ref ?? ""),
    }));
  }

  // mig 480 — the register of client numbers answers first (any of a job's
  // numbers, not just the one on the project row); the local guesses below
  // are the fallback while it is asked.
  const { data: regMatch } = useQuery({
    queryKey: ["client_ref_match", header.client_project_ref.trim(), header.client_site_code.trim()],
    enabled: !header.project_id && (header.client_project_ref.trim().length >= 3 || header.client_site_code.trim().length >= 3),
    queryFn: async () => {
      const { data } = await (supabase as any).schema("app").rpc("project_for_client_ref", { p_ref: header.client_project_ref.trim() || null, p_site_code: header.client_site_code.trim() || null });
      const row = Array.isArray(data) ? data[0] : data;
      return row?.project_id ? { project_id: row.project_id as string, why: String(row.why ?? "the client-number register") } : null;
    },
  });
  const suggested = useMemo(() => {
    if (header.project_id) return null;
    if (regMatch) { const p = projects.find(x => x.project_id === regMatch.project_id); if (p) return { p, why: regMatch.why }; }
    const ref = header.client_project_ref.trim().toLowerCase();
    const code = header.client_site_code.trim().toLowerCase();
    const name = header.client_site_name.trim().toLowerCase();
    if (ref) { const p = projects.find(x => (x.client_project_ref ?? "").trim().toLowerCase() === ref); if (p) return { p, why: "same client project number" }; }
    if (code) { const p = projects.find(x => (x.site?.carrier_site_id ?? "").trim().toLowerCase() === code); if (p) return { p, why: `site code ${header.client_site_code}` }; }
    if (name) {
      const first = name.split(/\s+/)[0];
      if (first.length >= 4) { const p = projects.find(x => (x.site?.site_name ?? "").toLowerCase().includes(first) || x.project_name.toLowerCase().includes(first)); if (p) return { p, why: "site name looks the same" }; }
    }
    return null;
  }, [header.project_id, header.client_project_ref, header.client_site_code, header.client_site_name, projects, regMatch]);

  // ---- snap / pick → upload → read --------------------------------------------
  async function pickFile(f: File | null) {
    if (!f) return;
    setReadError(null); setReadInfo(null);
    try {
      setReadStatus("uploading");
      const path = await uploadDocket(f);
      setDocPaths(prev => [...prev, path]);
      setReadStatus("reading");
      const ai = await readDocket(path);
      applyRead(ai);
      setAiRaw({ confidence: ai.confidence, raw: ai.extracted });
      setReadInfo({ confidence: ai.confidence, notes: ai.extracted.notes, handwritten: ai.extracted.handwritten_notes });
      setReadStatus("done");
    } catch (e: any) {
      setReadStatus("error"); setReadError(e?.message ?? "Couldn't read it");
    } finally {
      if (cameraInput.current) cameraInput.current.value = "";
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  // Read an already-attached docket again (after a reader change, or a first
  // read that failed). Replaces the lines — with a check if there are any.
  async function rereadDocket(path: string) {
    if (lines.length > 0 && !window.confirm("Replace the current lines with a fresh read of this docket?")) return;
    setReadError(null); setReadInfo(null);
    try {
      setReadStatus("reading");
      setLines([]);
      const ai = await readDocket(path);
      applyRead(ai);
      setAiRaw({ confidence: ai.confidence, raw: ai.extracted });
      setReadInfo({ confidence: ai.confidence, notes: ai.extracted.notes, handwritten: ai.extracted.handwritten_notes });
      setReadStatus("done");
    } catch (e: any) {
      setReadStatus("error"); setReadError(e?.message ?? "Couldn't read it");
    }
  }

  // Pre-fill only what's still blank — the person's typing wins. Lines are
  // appended (a second page / second docket in the same delivery adds lines).
  function applyRead(ai: DocketRead) {
    const x = ai.extracted;
    setHeader(h => ({
      ...h,
      received_at:        x.delivery_date && /^\d{4}-\d{2}-\d{2}$/.test(x.delivery_date) && x.delivery_date <= new Date().toISOString().slice(0, 10)
                            ? `${x.delivery_date}T${h.received_at.slice(11, 16) || "08:00"}` : h.received_at,
      client_project_ref: h.client_project_ref || (x.client_project_ref ?? ""),
      client_site_code:   h.client_site_code   || (x.client_site_code ?? ""),
      client_site_name:   h.client_site_name   || (x.client_site_name ?? ""),
      docket_number:      h.docket_number      || (x.docket_number ?? ""),
      supplier:           h.supplier           || (x.supplier ?? ""),
      carrier:            h.carrier            || (x.carrier ?? ""),
      signed_by:          h.signed_by          || (x.signed_by ?? ""),
      notes:              h.notes || (x.handwritten_notes ?? ""),
    }));
    setLines(prev => [
      ...prev,
      ...x.lines.map(l => newLine({
        part_number: l.part_number ?? "", client_material_no: l.client_material_no ?? "",
        description: l.description ?? "",
        qty_expected: l.qty_expected == null ? "" : String(l.qty_expected),
        qty_received: l.line_status === "backorder" ? "0" : (l.qty_expected == null ? "" : String(l.qty_expected)),
        uom: l.uom ?? "", serials: (l.serials ?? []).join(", "),
        line_status: l.line_status, note: l.note ?? "",
      })),
    ]);
  }

  // ---- lines -------------------------------------------------------------------
  const setLine = (key: string, patch: Partial<DeliveryLine>) =>
    setLines(prev => prev.map(l => l.key === key ? { ...l, ...patch } : l));
  const removeLine = (key: string) => setLines(prev => prev.filter(l => l.key !== key));

  // ---- save --------------------------------------------------------------------
  async function save() {
    if (docPaths.length === 0 && !window.confirm("No docket photo attached. Save the delivery without one?")) return;
    if (lines.length === 0 && !window.confirm("No line items. Save the delivery with just the header?")) return;
    setBusy(true);
    try {
      const id = await saveDelivery(editId, header, docPaths, lines, aiRaw);
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: ["delivery", id] });
      qc.invalidateQueries({ queryKey: ["project_deliveries"] });
      // mig 501 — the delivery ticks the Site BOM register by part number (manager+; a field docket is ticked when the office saves it)
      if (isManagerPlus) {
        try {
          const r = await applyDeliveryToBom(id);
          qc.invalidateQueries({ queryKey: ["project_materials"] }); qc.invalidateQueries({ queryKey: ["bom_lines"] });
          // mig 506 — James: "we won't always get it in a clean Excel sheet" — the docket's unknown lines can build the register
          if (r.unmatched.length > 0 && header.project_id) {
            const names = r.unmatched.map(u => `${u.part_number ?? "?"} ×${u.qty}`).join(", ");
            if (window.confirm(`${r.lines_ticked} BOM line${r.lines_ticked === 1 ? "" : "s"} ticked (${r.qty} items).\n\nNot on the job's BOM yet: ${names}.\n\nAdd ${r.unmatched.length === 1 ? "this line" : `these ${r.unmatched.length} lines`} to the job's register from this docket? (qty = what landed, marked "from docket"). Cancel leaves the register as it is.`)) {
              const a = await addDocketLinesToBom(id);
              qc.invalidateQueries({ queryKey: ["project_materials"] }); qc.invalidateQueries({ queryKey: ["bom_lines"] }); qc.invalidateQueries({ queryKey: ["bom_counts"] });
              window.alert(`${a.lines_added} line${a.lines_added === 1 ? "" : "s"} added to the register and ticked.`);
            }
          } else if (r.lines_ticked > 0 || r.unmatched.length > 0) window.alert(`${r.lines_ticked} BOM line${r.lines_ticked === 1 ? "" : "s"} ticked (${r.qty} items).${r.unmatched.length ? `\n\nNot on the job's BOM: ${r.unmatched.map(u => `${u.part_number ?? "?"} ×${u.qty}`).join(", ")} — put the delivery on a job to add them.` : ""}`);
        } catch (e: any) { console.warn("BOM tick failed:", e?.message ?? e); }
      }
      navigate(header.project_id ? `/projects/${header.project_id}?tab=materials` : (isManagerPlus ? "/materials" : "/capture"));
    } catch (e: any) {
      window.alert(`Couldn't save the delivery: ${e?.message ?? e}`);
    } finally { setBusy(false); }
  }

  if (!user) return <div className="p-8"><Spinner /></div>;
  const reading = readStatus === "uploading" || readStatus === "reading";
  const project = projects.find(p => p.project_id === header.project_id) ?? null;

  return (
    <div className="min-h-screen pb-24 px-4 pt-6 md:pt-8">
      <div className="max-w-3xl mx-auto">
        <Link to={editId && header.project_id ? `/projects/${header.project_id}?tab=materials` : (isManagerPlus ? "/materials" : "/capture")} className="inline-flex items-center gap-1 text-[12px] text-text-muted hover:text-text mb-3">
          <ArrowLeft size={13} /> Back
        </Link>
        <div className="mb-1 text-[11px] font-mono uppercase tracking-wider2 text-text-muted">Materials</div>
        <h1 className="text-2xl font-semibold tracking-tight">{editId ? "Edit delivery" : "Take a delivery"}</h1>
        <p className="text-text-muted text-[13px] mt-1.5">
          Snap the docket. Ops reads the project number, site and lines; you confirm the job and who signed.
        </p>

        {/* 1 · the docket -------------------------------------------------------- */}
        <Card className="mt-5">
          <div className="p-4">
            <input ref={cameraInput} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => pickFile(e.target.files?.[0] ?? null)} />
            <input ref={fileInput} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => pickFile(e.target.files?.[0] ?? null)} />
            <div className="text-[12px] text-text-muted mb-3">Whole slip in frame, phone flat over it, good light — the read is only as good as the photo. Check the part numbers against the paper before you save.</div>
            <div className="flex flex-wrap gap-2">
              <Button icon={Camera} onClick={() => cameraInput.current?.click()} disabled={reading}>Snap the docket</Button>
              <Button variant="secondary" icon={FileUp} onClick={() => fileInput.current?.click()} disabled={reading}>Photo or PDF from files</Button>
              {reading && <span className="inline-flex items-center gap-2 text-[12px] text-text-muted"><Spinner size={14} /> {readStatus === "uploading" ? "Uploading…" : "Reading the docket…"}</span>}
            </div>
            {readStatus === "done" && readInfo && (
              <div className="mt-3 text-[12px] text-text-muted flex items-start gap-2">
                <Sparkles size={14} className="text-accent mt-0.5 flex-shrink-0" />
                <div>
                  Read at {(readInfo.confidence * 100).toFixed(0)}% confidence — check the lines against the paper before saving.
                  {readInfo.notes && <div className="mt-1 text-warn">{readInfo.notes}</div>}
                </div>
              </div>
            )}
            {readStatus === "error" && (
              <div className="mt-3 text-[12px] text-danger flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <div>{readError} — the docket is attached; fill the lines in by hand.</div>
              </div>
            )}
            {docPaths.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {docPaths.map(p => {
                  const isPdf = p.toLowerCase().endsWith(".pdf");
                  const url = docUrls[p];
                  return (
                    <div key={p} className="relative">
                      {url && !isPdf
                        ? <img src={url} alt="Docket" className="h-28 w-28 object-cover rounded-md border border-line bg-ink" />
                        : <div className="h-28 w-28 rounded-md border border-line bg-ink flex items-center justify-center text-[11px] text-text-muted">{isPdf ? "PDF" : "…"}</div>}
                      {url && <a href={url} target="_blank" rel="noreferrer" className="absolute bottom-1 right-1 p-1 rounded bg-black/70 text-white" title="Open"><ExternalLink size={12} /></a>}
                      <button type="button" onClick={() => rereadDocket(p)} disabled={reading} className="absolute bottom-1 left-1 p-1 rounded bg-black/70 text-white disabled:opacity-50" title="Read this docket again"><RefreshCw size={12} /></button>
                      <button type="button" onClick={() => setDocPaths(prev => prev.filter(x => x !== p))} className="absolute top-1 right-1 p-1 rounded bg-black/70 text-white" title="Remove from this delivery"><Trash2 size={12} /></button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* 2 · which job --------------------------------------------------------- */}
        <Card className="mt-4">
          <div className="p-4 space-y-4">
            <div className="text-[11px] font-mono uppercase tracking-wider2 text-text-muted flex items-center gap-2"><Truck size={13} /> Which job</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Client project no." hint="Mirage no. on APD slips · R009-… on Ventia dockets">
                <Input mono value={header.client_project_ref} onChange={e => setHeader({ ...header, client_project_ref: e.target.value })} placeholder="4022081.01" />
              </Field>
              <Field label="Site code" hint="LRD / NBN site id">
                <Input mono value={header.client_site_code} onChange={e => setHeader({ ...header, client_site_code: e.target.value })} placeholder="ANWM" />
              </Field>
              <Field label="Site name (as printed)">
                <Input value={header.client_site_name} onChange={e => setHeader({ ...header, client_site_name: e.target.value })} placeholder="ANDREWS WEST CMTS" />
              </Field>
            </div>
            <Field label="Ops project" hint={project ? `Site: ${project.site?.site_name ?? "—"}${project.client_project_ref ? ` · client no. ${project.client_project_ref}` : " · no client project no. on the job yet — it will be set from this slip"}` : "Leave blank if you're not sure — a manager matches it from the register."}>
              <Select value={header.project_id ?? ""} onChange={e => applyProject(projects.find(p => p.project_id === e.target.value) ?? null)}>
                <option value="">— not matched yet —</option>
                {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_code} · {p.project_name}</option>)}
              </Select>
            </Field>
            {suggested && (
              <div className="text-[12px] flex flex-wrap items-center gap-2 rounded-md border border-accent/40 bg-accent/8 px-3 py-2">
                <span>Looks like <span className="font-medium">{suggested.p.project_code} · {suggested.p.project_name}</span> <span className="text-text-muted">({suggested.why})</span></span>
                <Button size="sm" onClick={() => applyProject(suggested.p)}>Use it</Button>
              </div>
            )}
          </div>
        </Card>

        {/* 3 · how it came in ---------------------------------------------------- */}
        <Card className="mt-4">
          <div className="p-4 space-y-4">
            <div className="text-[11px] font-mono uppercase tracking-wider2 text-text-muted">How it came in</div>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(SOURCE_LABEL) as DeliverySource[]).map(s => (
                <button key={s} type="button" onClick={() => setHeader({ ...header, source: s })}
                  className={`px-3 py-1.5 rounded-full text-[12px] border ${header.source === s ? "bg-accent/15 border-accent text-text" : "border-line text-text-muted hover:text-text"}`}>
                  {SOURCE_LABEL[s]}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Docket / con-note no."><Input mono value={header.docket_number} onChange={e => setHeader({ ...header, docket_number: e.target.value })} /></Field>
              <Field label="Supplier" hint="CEVA, Ericsson, Warren & Brown, DECON, Ventia…"><Input value={header.supplier} onChange={e => setHeader({ ...header, supplier: e.target.value })} /></Field>
              <Field label="Carrier" hint="COPE, TFM Express, Big Post…"><Input value={header.carrier} onChange={e => setHeader({ ...header, carrier: e.target.value })} /></Field>
              <Field label="Received"><Input type="datetime-local" value={header.received_at} onChange={e => setHeader({ ...header, received_at: e.target.value })} /></Field>
              <Field label="Received by (Coretel)">
                <Select value={header.received_by_employee_id ?? ""} onChange={e => setHeader({ ...header, received_by_employee_id: e.target.value || null })}>
                  <option value="">—</option>
                  {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.first_name} {e.last_name}</option>)}
                </Select>
              </Field>
              <Field label="Signed by (name on the docket)"><Input value={header.signed_by} onChange={e => setHeader({ ...header, signed_by: e.target.value })} /></Field>
            </div>
            <Field label="Notes" hint="Handwriting on the slip, shortages, where it's stored at the depot…">
              <Textarea rows={2} value={header.notes} onChange={e => setHeader({ ...header, notes: e.target.value })} />
            </Field>
          </div>
        </Card>

        {/* 4 · the lines --------------------------------------------------------- */}
        <Card className="mt-4">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-mono uppercase tracking-wider2 text-text-muted">What was on it · {lines.length} line{lines.length === 1 ? "" : "s"}</div>
              <Button size="sm" variant="secondary" icon={Plus} onClick={() => setLines(prev => [...prev, newLine()])}>Add line</Button>
            </div>
            {lines.length === 0 && <div className="text-[12px] text-text-muted">No lines yet — snap the docket or add them by hand.</div>}
            <div className="space-y-3">
              {lines.map((l, i) => (
                <div key={l.key} className="rounded-md border border-line p-3 bg-ink-raised/40">
                  <div className="flex items-start gap-2">
                    <div className="text-[11px] font-mono text-text-dim pt-3 w-5">{i + 1}</div>
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-6 gap-2">
                      <div className="col-span-2 md:col-span-3">
                        <Input value={l.description} placeholder="Description" onChange={e => setLine(l.key, { description: e.target.value })} />
                      </div>
                      <Input mono value={l.part_number} placeholder="Part no." onChange={e => setLine(l.key, { part_number: e.target.value })} />
                      <Input mono value={l.client_material_no} placeholder="Client item no." onChange={e => setLine(l.key, { client_material_no: e.target.value })} />
                      <Input value={l.uom} placeholder="UOM" onChange={e => setLine(l.key, { uom: e.target.value })} />
                      <Input mono inputMode="decimal" value={l.qty_expected} placeholder="Qty on slip" onChange={e => setLine(l.key, { qty_expected: e.target.value })} />
                      <Input mono inputMode="decimal" value={l.qty_received} placeholder="Qty received" onChange={e => setLine(l.key, { qty_received: e.target.value })} />
                      <Select value={l.line_status} onChange={e => setLine(l.key, { line_status: e.target.value as LineStatus })}>
                        {(Object.keys(LINE_STATUS_LABEL) as LineStatus[]).map(s => <option key={s} value={s}>{LINE_STATUS_LABEL[s]}</option>)}
                      </Select>
                      <div className="col-span-2 md:col-span-3">
                        <Input value={l.note} placeholder="Note on this line (allocation, 'waiting on stock'…)" onChange={e => setLine(l.key, { note: e.target.value })} />
                      </div>
                      <div className="col-span-2 md:col-span-6">
                        <Input mono value={l.serials} placeholder="Serials (comma separated) — optional, usually recorded at first-day prep" onChange={e => setLine(l.key, { serials: e.target.value })} />
                      </div>
                    </div>
                    <button type="button" onClick={() => removeLine(l.key)} className="p-2 text-text-dim hover:text-danger" title="Remove line"><Trash2 size={14} /></button>
                  </div>
                  {l.line_status !== "received" && <div className="mt-1 ml-7"><Pill small tone="warning" label={LINE_STATUS_LABEL[l.line_status]} /></div>}
                </div>
              ))}
            </div>
          </div>
        </Card>

        <div className="mt-5 flex items-center gap-2">
          <Button onClick={save} disabled={busy || reading}>{busy ? "Saving…" : (editId ? "Save changes" : "Save delivery")}</Button>
          {!header.project_id && <span className="text-[12px] text-text-muted">Not matched to a job yet — it will show in the register for a manager to match.</span>}
        </div>
      </div>
    </div>
  );
}
